<script setup>
const route = useRoute()
const user = useStrapiUser()
const apiVacancy = useApiVacancy()
const apiCompany = useApiCompany()

const { generateMeta } = useMetaUtils()
const { upload } = useUploadUtils()
const { getVacancyReferralUrl } = useReferrals()
const { mapProfile, mapCultures } = useAssessmentMapper()
const { confirm } = useConfirm()

const { data: vacancy } = await useAsyncData(
  `vacancy-${route.params.slug}`,
  () => apiVacancy.getVacancyBySlug(route.params.slug),
)

if (!vacancy.value) {
  navigateTo({ name: 'not-found' })
}

const { data: company } = await useAsyncData(
  `vacancy-${route.params.slug}-company`,
  () => getCompany(),
)
const { data: similarVacancies } = await useLazyAsyncData(
  `vacancy-${route.params.slug}-similar`,
  () => apiVacancy.getSimilarVacancies(vacancy.value.id),
  {
    watch: [vacancy],
  },
)

const managerUsername = computed(() => vacancy.value?.manager?.email)
const isProfessional = computed(() => user.value?.type === 'professional')
const isOwner = computed(
  () => user.value?.company?.id === vacancy.value?.company?.id,
)
const employmentType = computed(() => {
  const tag = vacancy.value?.tags?.find(
    (tag) => tag?.parent?.slug === 'dienstverband',
  )
  return tag?.name
})
const totalMatchScore = computed(() => vacancy.value?.matchingscore_total)

const hasPremium = computed(
  () => user.value?.type === 'company' && user.value?.company?.hasPremium,
)
const mappedProfile = computed(() => mapProfile(vacancy.value?.profile))
const mappedCulture = computed(() =>
  mapCultures(
    vacancy.value?.company?.profile?.topCultureRanking.map(
      (culture) => culture.cultureCharacteristic,
    ),
  ),
)
const personalityProfile = computed(
  () =>
    vacancy.value?.profile?.rolesRanking &&
    vacancy.value?.company?.profile?.topCultureRanking,
)
const cognitiveProfile = computed(() => vacancy.value?.profile?.education || '')
const motivationsProfile = computed(
  () => vacancy.value?.profile?.motivationsRanking || '',
)

async function getCompany() {
  return (
    await apiCompany.getCompanies({
      filters: {
        vacancies: {
          slug: {
            $eq: route.params.slug,
          },
        },
      },
    })
  )?.data?.[0]
}

async function deleteVacancy() {
  const confirmed = await confirm({
    title: 'Vacature verwijderen',
    text: 'Weet je zeker dat je deze vacature wilt verwijderen? De actie kan niet ongedaan gemaakt worden.',
    confirmText: 'Verwijderen',
    cancelText: 'Annuleer',
  })

  if (confirmed) {
    await apiVacancy.deleteVacancy(vacancy.value.id)
    navigateTo('/vacancies')
  }
}

const { title, meta, script } = generateMeta(vacancy.value)

useSeoMeta({
  title,
  meta,
})

if (script.length) {
  useHead({
    script,
  })
}
</script>

<template>
  <div v-if="vacancy" class="bg-white lg:bg-gray-50">
    <div class="bg-primary pb-6 pt-[58px] lg:pt-[68px]">
      <BaseContainer class="relative">
        <div class="flex flex-col items-center lg:flex-row">
          <figure
            class="relative mb-4 h-[80px] w-[80px] justify-self-center overflow-hidden rounded-lg bg-gray-200 lg:mb-0"
            :class="{
              'flex items-center bg-[#F8FAFC]': vacancy && !vacancy.image,
            }"
          >
            <img
              v-if="vacancy?.image"
              :src="upload(vacancy.image)"
              alt=""
              class="absolute left-0 top-0 h-full w-full object-cover"
            />
            <img v-else class="mx-auto" src="/images/fallback.svg" />
          </figure>
          <div class="flex flex-col">
            <div v-if="vacancy?.title" class="pl-0 pt-3 lg:pl-8 max-w-full">
              <h1
                class="text-center font-heading text-6xl font-medium uppercase break-words text-secondary lg:text-left lg:text-7xl"
              >
                {{ vacancy.title || '' }}
              </h1>
            </div>
            <div v-if="vacancy?.subtitle" class="pl-0 lg:pl-8 max-w-2xl">
              <p
                class="text-center text-sm break-words text-secondary lg:text-left"
              >
                {{ vacancy.subtitle || '' }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-8 flex items-center justify-between md:px-0 md:py-0 lg:mt-12 lg:px-0"
        >
          <div class="md:flex md:gap-4">
            <div class="mb-3 flex items-center md:mb-0">
              <IconOffice class="mr-2 h-[24px] w-[24px] text-secondary" />
              <div
                v-if="vacancy?.company?.name"
                class="text-base text-gray-800"
              >
                <NuxtLink
                  :to="`/companies/${vacancy?.company?.slug}`"
                  class="underline"
                >
                  {{ vacancy.company.name }}
                </NuxtLink>
              </div>
            </div>

            <div
              v-if="managerUsername"
              class="mb-3 flex items-center md:mb-0 lg:ml-8"
            >
              <IconMail class="mr-2 h-[24px] w-[24px] text-secondary" />
              <a
                :href="`mailto:${managerUsername}`"
                target="_blank"
                class="text-base text-gray-800 underline"
                >{{ managerUsername }}</a
              >
            </div>

            <div v-if="employmentType" class="flex items-center lg:ml-8">
              <IconBriefcase class="mr-2 h-[24px] w-[24px] text-secondary" />
              <div class="text-base text-gray-800">{{ employmentType }}</div>
            </div>
          </div>
        </div>
      </BaseContainer>
    </div>
    <div class="h-[60px] w-full bg-primary lg:h-[100px]"></div>
    <div class="mt-[-60px] lg:mt-[-100px]">
      <BaseContainer>
        <div
          v-if="totalMatchScore && vacancy"
          class="rounded-md border border-solid border-gray-400 bg-white p-6 lg:hidden"
        >
          <h3
            class="text-md mb-6 text-center font-sans font-semibold text-secondary"
          >
            Match met vacature
          </h3>

          <VacanciesDetailMatch :vacancy="vacancy" :match="totalMatchScore" />
        </div>

        <div class="-mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
          <div class="gap-x-6 lg:flex lg:gap-x-8">
            <div
              class="relative h-auto w-full bg-white pt-6 lg:mb-0 lg:w-8/12 lg:p-8"
            >
              <TagsByGroup :tags="vacancy?.tags" class="mt-6" />

              <div class="pt-5">
                <div v-for="(content, index) in vacancy.content" :key="index">
                  <VacanciesDetailContent
                    :key="index"
                    :content="content"
                    :is-first="index === 0"
                  />

                  <SliderWidget
                    v-if="vacancy.media?.items?.length && index === 0"
                    class="aspect-video w-full"
                    :items="vacancy.media.items || []"
                    theme="minimal"
                  />
                </div>
                <div class="my-8 flex justify-between">
                  <ButtonInterest
                    v-if="isProfessional && vacancy?.id"
                    target-type="vacancy"
                    :target-id="vacancy?.id"
                    class="mb-2"
                  />
                </div>
              </div>

              <div v-if="isOwner && hasPremium" class="mb-8">
                <p>Deel uw vacature met deze link:</p>
                <UrlCopier :url="getVacancyReferralUrl(vacancy)" />
              </div>
            </div>

            <div class="w-full lg:w-4/12">
              <div
                v-if="totalMatchScore"
                class="mb-8 rounded-md border border-solid border-gray-400 bg-white p-6 hidden lg:block"
              >
                <h3
                  class="text-md mb-6 text-center font-sans font-semibold text-secondary"
                >
                  Match met vacature
                </h3>

                <VacanciesDetailMatch
                  v-if="vacancy"
                  :vacancy="vacancy"
                  :match="totalMatchScore"
                />
              </div>

              <VacanciesDetailCompany
                v-if="company"
                class="hidden lg:block"
                :company
              />

              <div
                v-if="company"
                class="mt-5 hidden lg:flex items-center gap-3 lg:justify-between"
              >
                <Button
                  v-if="isOwner && vacancy?.id"
                  :path="`/vacancies/${vacancy.slug}/edit`"
                  theme="secondary"
                  text="wijzig vacature"
                  size="md"
                />

                <Button
                  v-if="isOwner && vacancy?.id"
                  theme="primary"
                  text="verwijder vacature"
                  size="md"
                  @click="deleteVacancy"
                />
              </div>
            </div>
          </div>
        </div>
      </BaseContainer>
      <BaseContainer class="bg-gray-50 pb-16">
        <VacanciesDetailCompany v-if="company" class="lg:hidden" :company />

        <div
          v-if="company"
          class="lg:hidden mt-5 flex items-center justify-between gap-3"
        >
          <Button
            v-if="isOwner && vacancy?.id"
            theme="secondary"
            text="wijzig vacature"
            size="md"
            path="edit"
          />

          <Button
            v-if="isOwner && vacancy?.id"
            theme="primary"
            text="verwijder vacature"
            size="md"
            @click="deleteVacancy"
          />
        </div>

        <div class="mt-8 lg:mt-16">
          <h2
            class="hidden font-heading text-5xl font-medium uppercase text-secondary lg:block"
          >
            Wat we zoeken voor deze vacature
          </h2>
          <div class="mt-8 lg:flex lg:gap-x-4">
            <div
              v-if="personalityProfile?.length > 0"
              class="mb-8 rounded-md border border-solid border-gray-400 bg-white pt-8 lg:mb-0 lg:w-3/12"
            >
              <h3
                class="text-md text-center font-sans font-semibold text-secondary"
              >
                Persoonlijkheid
              </h3>
              <VacanciesDetailAssessment
                :ranking="mappedProfile.topOperationalRoles"
                type="persoonlijkheid"
                color="#d3daf7"
                icon-source="/images/illustration-personality.svg"
              />
            </div>

            <div
              v-if="motivationsProfile?.length > 0"
              class="mb-8 rounded-md border border-solid border-gray-400 bg-white pt-8 lg:mb-0 lg:w-3/12"
            >
              <h3
                class="text-md text-center font-sans font-semibold text-secondary"
              >
                Drijfveren
              </h3>
              <VacanciesDetailAssessment
                :ranking="mappedProfile.topMotivations"
                type="drijfveren"
                color="#f7d3d3"
                icon-source="/images/illustration-motivation.svg"
              />
            </div>

            <div
              v-if="mappedCulture?.length > 0"
              class="mb-8 rounded-md border border-solid border-gray-400 bg-white pt-8 lg:mb-0 lg:w-3/12"
            >
              <h3
                class="text-md text-center font-sans font-semibold text-secondary"
              >
                Cultuur
              </h3>
              <VacanciesDetailAssessment
                :ranking="mappedCulture"
                type="cultuur"
                color="#d3daf7"
                icon-source="/images/illustration-personality.svg "
              />
            </div>

            <div
              v-if="cognitiveProfile?.id"
              class="mb-8 rounded-md border border-solid border-gray-400 bg-white pt-8 lg:mb-0 lg:w-3/12"
            >
              <h3
                class="text-md text-center font-sans font-semibold text-secondary"
              >
                Cognitief
              </h3>
              <VacanciesDetailAssessment
                :min="cognitiveProfile.min"
                :max="cognitiveProfile.max"
                type="denkniveau"
                color="#d3daf7"
                icon-source="/images/illustration-cognitive.svg"
              />
            </div>
          </div>
        </div>
      </BaseContainer>
    </div>
    <div class="bg-white">
      <BaseContainer>
        <div
          v-if="similarVacancies && similarVacancies.length > 0"
          class="pb-16 pt-16"
        >
          <h2
            class="mb-8 hidden font-heading text-5xl font-medium uppercase text-secondary lg:block"
          >
            Relevante functies
          </h2>
          <div class="lg:grid lg:grid-cols-3 lg:gap-6">
            <VacanciesCard
              v-for="similarVacancy in similarVacancies"
              :key="similarVacancy.id"
              :item="similarVacancy"
              type="vacancy"
            />
          </div>
        </div>
      </BaseContainer>
    </div>
  </div>
</template>
